import type React from 'react';
import type {
  FmMapSnapshotProps,
  FmMapSnapshotState,
  FmMapStageTestIds,
} from './FmMapStage.types';

export const cx = (...parts: Array<string | false | null | undefined>): string =>
  parts.filter(Boolean).join(' ');

export const mapChromeClass = (base: string, surface: 'plain' | 'glass') =>
  cx(base, surface === 'glass' && `${base}--glass`);

export const mapSnapshotHeight = (height: number | string): React.CSSProperties =>
  typeof height === 'number' ? { height, minHeight: height } : { height, minHeight: height };

export const mapStageTestIds = (
  dataTestId?: string,
  actionsTestId?: string,
  leftChromeTestId?: string,
  rightChromeTestId?: string,
): FmMapStageTestIds => ({
  actions: actionsTestId ?? (dataTestId ? `${dataTestId}-actions` : undefined),
  leftChrome: leftChromeTestId ?? (dataTestId ? `${dataTestId}-left-chrome` : undefined),
  rightChrome: rightChromeTestId ?? (dataTestId ? `${dataTestId}-right-chrome` : undefined),
});

export const mapStageStyle = (
  fill: boolean,
  stageHeight?: number,
): React.CSSProperties =>
  fill
    ? { height: '100%', minHeight: stageHeight }
    : { height: stageHeight, minHeight: stageHeight };

export const mapSnapshotState = ({
  height,
  size,
  aspectRatio = 'wide',
  variant = 'outlined',
  fit = 'cover',
  interactionMode,
  className,
}: Pick<
  FmMapSnapshotProps,
  'height' | 'size' | 'aspectRatio' | 'variant' | 'fit' | 'interactionMode' | 'className'
>): FmMapSnapshotState => {
  const useSizeClass = height === undefined;
  const resolvedSize = size ?? 'standard';
  const resolvedVariant = variant === 'borderless' ? 'media' : variant;
  const resolvedInteractionMode = interactionMode ?? 'staticSnapshot';
  const isInteractive = resolvedInteractionMode === 'interactive';
  const interactionModeClass = isInteractive
    ? 'fm-map-snapshot--interactive'
    : 'fm-map-snapshot--static-snapshot';

  return {
    frameStyle: useSizeClass ? undefined : mapSnapshotHeight(height),
    resolvedInteractionMode,
    isInteractive,
    className: cx(
      'fm-map-snapshot',
      `fm-map-snapshot--${aspectRatio}`,
      `fm-map-snapshot--fit-${fit}`,
      resolvedVariant !== 'outlined' && `fm-map-snapshot--${resolvedVariant}`,
      useSizeClass && `fm-map-snapshot--size-${resolvedSize}`,
      interactionModeClass,
      className,
    ),
  };
};
